import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { Prediction } from '../types';
import { supabase, isDemoMode } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { queryCache, CACHE_TTL } from '../lib/cache';

interface PredictionsContextType {
  allPredictions: Prediction[];
  loading: boolean;
  error: string | null;
  refreshPredictions: () => Promise<void>;
  getUserPredictions: (userId: string, entryId?: string | null) => Prediction[];
  getPredictionForGame: (userId: string, gameId: string, entryId?: string | null) => Prediction | undefined;
}

const PredictionsContext = createContext<PredictionsContextType | undefined>(undefined);

export const usePredictionsContext = () => {
  const context = useContext(PredictionsContext);
  if (!context) {
    throw new Error('usePredictionsContext must be used within PredictionsProvider');
  }
  return context;
};

interface PredictionsProviderProps {
  children: ReactNode;
}

export const PredictionsProvider: React.FC<PredictionsProviderProps> = ({ children }) => {
  const [allPredictions, setAllPredictions] = useState<Prediction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentOrganization, activeCompetitionId } = useAuth();

  const fetchPredictions = useCallback(async () => {
    if (isDemoMode) {
      setAllPredictions([]);
      setLoading(false);
      return;
    }

    if (!currentOrganization?.id || !activeCompetitionId) {
      setAllPredictions([]);
      setLoading(false);
      return;
    }

    // v2: Added explicit .limit(10000) to fix Supabase's default 1000-row limit
    const cacheKey = queryCache.generateKey('all_org_predictions_v2', {
      orgId: currentOrganization.id,
      competitionId: activeCompetitionId
    });

    try {
      setLoading(true);
      setError(null);

      const result = await queryCache.deduplicate(
        cacheKey,
        async () => {
          // Supabase/PostgREST has a hard limit of 1000 rows per request.
          // We need to paginate to get all predictions.
          const allData: any[] = [];
          let from = 0;
          const batchSize = 1000;
          let hasMore = true;

          while (hasMore) {
            const { data, error } = await supabase
              .from('predictions')
              .select('id, user_id, game_id, home_score, away_score, competition_id, entry_id, created_at, updated_at')
              .eq('organization_id', currentOrganization.id!)
              .eq('competition_id', activeCompetitionId)
              .order('created_at', { ascending: false })
              .range(from, from + batchSize - 1);

            if (error) {
              throw error;
            }

            allData.push(...data);
            
            // If we got less than batchSize, we've fetched everything
            if (data.length < batchSize) {
              hasMore = false;
            } else {
              from += batchSize;
            }
          }

          console.log(`[PredictionsContext] Fetched ${allData.length} predictions for competition ${activeCompetitionId} (paginated)`);

          const formattedPredictions: Prediction[] = allData.map(row => ({
            id: row.id,
            gameId: row.game_id,
            homeScore: row.home_score,
            awayScore: row.away_score,
            userId: row.user_id,
            competitionId: row.competition_id,
            entryId: row.entry_id ?? undefined,
            createdAt: new Date(row.created_at),
            updatedAt: new Date(row.updated_at)
          }));

          return formattedPredictions;
        },
        CACHE_TTL.SHORT
      );

      setAllPredictions(result);
    } catch (err) {
      console.error('Error fetching all predictions:', err);
      setError('Failed to load predictions');
    } finally {
      setLoading(false);
    }
  }, [currentOrganization?.id, activeCompetitionId]);

  useEffect(() => {
    // Clear state immediately when competition changes to prevent stale data
    setAllPredictions([]);
    setLoading(true);

    if (!currentOrganization?.id || !activeCompetitionId) {
      setLoading(false);
      return;
    }

    fetchPredictions();
  }, [currentOrganization?.id, activeCompetitionId, fetchPredictions]);

  const getUserPredictions = useCallback((userId: string, entryId?: string | null) => {
    return allPredictions.filter(p =>
      p.userId === userId && (entryId == null || p.entryId === entryId)
    );
  }, [allPredictions]);

  const getPredictionForGame = useCallback((userId: string, gameId: string, entryId?: string | null) => {
    return allPredictions.find(p =>
      p.userId === userId
      && p.gameId === gameId
      && (entryId == null || p.entryId === entryId)
    );
  }, [allPredictions]);

  const value: PredictionsContextType = useMemo(() => ({
    allPredictions,
    loading,
    error,
    refreshPredictions: fetchPredictions,
    getUserPredictions,
    getPredictionForGame
  }), [allPredictions, loading, error, fetchPredictions, getUserPredictions, getPredictionForGame]);

  return (
    <PredictionsContext.Provider value={value}>
      {children}
    </PredictionsContext.Provider>
  );
};
